import { hasNonEmptyValue } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import * as oPath from '../../../../Lib/Object/Path/Immutable';
import { ILinkProps, Link } from '../../../Core';
import './WorksheetLink.scss';

type WorksheetLinkProps = ILinkProps & {
  /**
   * Truncates link text to a single line (with an ellipsis)
   * Used to avoid overflow in layouts that do not support it
   */
  truncate?: boolean;
  worksheetPath: string;
  shortNameOnly?: boolean;
};

/**
 * Component that will generate a link to a worksheet from the path to the worksheet model in the app state
 */
const WorksheetLink: React.FC<WorksheetLinkProps> = ({ truncate, worksheetPath, shortNameOnly, ...otherProps }) => {
  const linkDetails = useSelector(
    pipe(
      (s) => oPath.get(s, `${worksheetPath}.syncModel`),
      (worksheet) =>
        hasNonEmptyValue(worksheet?.header?.TopLevelTest)
          ? // Link to top-level test where it exists
            {
              id: worksheet.header.TopLevelTest.LinkId,
              caption: shortNameOnly
                ? worksheet.header.TopLevelTest.WorksheetType.ShortName
                : worksheet.header.TopLevelTest.WorksheetType.Caption,
            }
          : // If not, this is the top-level test and we link to it directly
            { id: worksheet.id, caption: shortNameOnly ? worksheet.type.ShortName : worksheet.type.Caption }
    ),
    deepEquals
  );

  return (
    <div className={classNames('WorksheetLink', { 'WorksheetLink-truncate': truncate })}>
      <Link to={`/worksheets/${linkDetails.id}`} {...otherProps}>
        {linkDetails.caption}
      </Link>
    </div>
  );
};

export default WorksheetLink;
export { WorksheetLink };
